import { type FollowUpSkillItemResponse, SKILL_SCORE_OPTIONS } from '@lms/shared'
import { Send } from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { groupSkillItemsByCategory } from '@/lib/skill-check'

interface SkillCheckEvaluationFormProps {
  title: string
  description: string
  skillItems: FollowUpSkillItemResponse[]
  isSubmitting: boolean
  /** 成功した場合のみ true を返す（失敗時は入力を残す） */
  onSubmit: (scores: { itemCode: string; score: number }[]) => Promise<boolean>
}

export function SkillCheckEvaluationForm({
  title,
  description,
  skillItems,
  isSubmitting,
  onSubmit,
}: SkillCheckEvaluationFormProps) {
  const [scores, setScores] = useState<Record<string, number>>({})
  const unansweredCount = skillItems.filter((item) => scores[item.code] === undefined).length

  const handleSubmit = async () => {
    if (!confirm(`${title}を提出します。提出後は修正できません。よろしいですか？`)) return
    const succeeded = await onSubmit(
      skillItems.map((item) => ({ itemCode: item.code, score: scores[item.code] })),
    )
    if (succeeded) setScores({})
  }

  return (
    <div className="space-y-4 rounded-md border p-4">
      <div>
        <h4 className="font-medium">{title}</h4>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {groupSkillItemsByCategory(skillItems).map((group) => (
        <fieldset key={group.category} className="space-y-3">
          <legend className="text-sm font-semibold">{group.category}</legend>
          {group.items.map((item) => (
            <div
              key={item.code}
              className="flex flex-col gap-2 border-b pb-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
            >
              <p className="text-sm sm:max-w-md">{item.label}</p>
              <RadioGroup
                aria-label={item.label}
                className="flex gap-3"
                value={scores[item.code]?.toString() ?? ''}
                onValueChange={(value) =>
                  setScores((prev) => ({ ...prev, [item.code]: Number(value) }))
                }
              >
                {SKILL_SCORE_OPTIONS.map(({ score, label }) => {
                  const id = `${item.code}-${score}`
                  return (
                    <div key={score} className="flex items-center gap-1" title={label}>
                      <RadioGroupItem id={id} value={score.toString()} />
                      <Label htmlFor={id} className="tabular-nums">
                        {score}
                      </Label>
                    </div>
                  )
                })}
              </RadioGroup>
            </div>
          ))}
        </fieldset>
      ))}

      <div className="flex items-center justify-end gap-3">
        {unansweredCount > 0 && (
          <span className="text-xs text-muted-foreground">未回答 {unansweredCount} 件</span>
        )}
        <Button size="sm" onClick={handleSubmit} disabled={unansweredCount > 0 || isSubmitting}>
          <Send className="mr-1 h-3.5 w-3.5" />
          {isSubmitting ? '送信中...' : `${title}を提出`}
        </Button>
      </div>
    </div>
  )
}
